import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useI18n, useLocalizedPath, NLink, useLanguageSwitcher, SUPPORTED_LANGS } from "@/i18n/I18nProvider";
import { Menu, X } from "lucide-react";

const NAV_KEYS = [
  ["/", "home"],
  ["/about", "about"],
  ["/soccer-camps", "camps"],
  ["/coach-education", "coachEd"],
  ["/partners", "partners"],
  ["/legal", "legal"],
  ["/contact", "contact"],
];

const linkTone = (active) => (active ? "text-[#C4A46A]" : "text-white/80 hover:text-[#C4A46A]");

const useScrolled = (threshold = 12) => {
  const [scrolled, setScrolled] = useState(() => typeof window !== "undefined" && window.scrollY > threshold);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

const BrandLink = () => (
  <NLink to="/" className="flex items-center gap-3 group" data-testid="brand-link">
    <img src="/nc-logo.png" alt="Northcrest" width={40} height={40} className="block object-contain" />
    <div className="leading-[1.05]">
      <div className="font-display text-white text-[15px] tracking-[0.32em]">NORTHCREST</div>
      <div className="font-display text-[#C4A46A] text-[10px] tracking-[0.5em]">ATHLETICS</div>
    </div>
  </NLink>
);

const DesktopNav = ({ t, isActive }) => (
  <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
    {NAV_KEYS.map(([p, key]) => (
      <NLink
        key={key}
        to={p}
        data-testid={`nav-${key}`}
        className={`text-[12px] tracking-[0.24em] uppercase font-bold whitespace-nowrap transition-colors ${linkTone(isActive(p))}`}
      >
        {t.nav[`${key}Short`] || t.nav[key]}
      </NLink>
    ))}
  </nav>
);

const LangSwitcher = ({ lang, switchTo }) => (
  <div className="hidden md:flex items-center gap-1 text-[11px] tracking-[0.2em]" data-testid="lang-switcher">
    {SUPPORTED_LANGS.map((l, i) => (
      <React.Fragment key={l}>
        {i > 0 && <span className="text-white/30">·</span>}
        <a
          href={switchTo(l)}
          data-testid={`lang-${l}`}
          className={`px-1.5 py-1 uppercase font-bold transition-colors ${l === lang ? "text-[#C4A46A]" : "text-white/70 hover:text-white"}`}
        >
          {l}
        </a>
      </React.Fragment>
    ))}
  </div>
);

const MobileMenu = ({ t, lang, switchTo }) => (
  <div data-testid="mobile-menu" className="lg:hidden bg-[#0F1B2D] border-t border-white/10">
    <div className="px-6 py-6 flex flex-col gap-4">
      {NAV_KEYS.map(([p, key]) => (
        <NLink key={key} to={p} data-testid={`mobile-nav-${key}`} className="text-white text-[13px] tracking-[0.2em] uppercase font-bold hover:text-[#C4A46A]">
          {t.nav[key]}
        </NLink>
      ))}
      <div className="pt-4 border-t border-white/10 flex items-center gap-3">
        {SUPPORTED_LANGS.map((l) => (
          <a key={l} href={switchTo(l)} className={`text-[12px] uppercase tracking-[0.2em] font-bold ${l === lang ? "text-[#C4A46A]" : "text-white/70"}`}>
            {l}
          </a>
        ))}
      </div>
      <NLink to="/contact" data-testid="mobile-book-cta" className="mt-2 inline-flex justify-center px-5 py-3 bg-[#C4A46A] text-[#0F1B2D] text-[11px] tracking-[0.24em] uppercase font-bold">
        {t.nav.bookCta}
      </NLink>
    </div>
  </div>
);

const Navbar = () => {
  const { t, lang } = useI18n();
  const make = useLocalizedPath();
  const switchTo = useLanguageSwitcher();
  const location = useLocation();
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);

  // Close mobile menu when route changes (skip initial mount).
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setOpen(false);
    }
  }, [location.pathname]);

  const isActive = (path) => {
    const localized = make(path);
    return path === "/" ? location.pathname === localized : location.pathname.startsWith(localized);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#0F1B2D]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <BrandLink />
        <DesktopNav t={t} isActive={isActive} />
        <div className="flex items-center gap-4">
          <LangSwitcher lang={lang} switchTo={switchTo} />
          <NLink
            to="/contact"
            data-testid="nav-book-cta"
            className="hidden md:inline-flex items-center px-5 py-3 bg-[#C4A46A] text-[#0F1B2D] text-[11px] tracking-[0.24em] uppercase font-bold hover:bg-[#A38755] transition-colors"
          >
            {t.nav.bookCta}
          </NLink>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="lg:hidden text-white p-2" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && <MobileMenu t={t} lang={lang} switchTo={switchTo} />}
    </header>
  );
};

export default Navbar;
